import PropTypes from 'prop-types';
import {useEffect} from 'react';
import PaymentOptionsLoadingStatus from '../../enums/PaymentOptionsLoadingStatus';
import doMetric from '../../v2/tracking/bigQueryMetricRecorder';
import forwardToURL from '../../v2/utils/forwardToURL';
import snakeToKebabCase from '../../v2/utils/snakeToKebabCase';
import Track from '../../v2/utils/tracking';
import AdyenV2CheckoutLazyLoader from './AdyenV2CheckoutLazyLoader';
import getAPIBillingAddressFromAdyenV2BillingAddress from './getAPIBillingAddressFromAdyenV2BillingAddress';

export default function useAdyenV2SessionLoader(props) {
  const getAdyenConfiguration = (clientKey, id, sessionData) => {
    let environment = props.adyenV2Environment;
    
    if (!environment) {
      environment = 'test';
      Track.error('Adyen V2 session environment not set, defaulting to test environment, if this is not intended set adyen_v2_environment setting to live, all possible values: \'test\' | \'live\' | \'live-us\' | \'live-au\' | \'live-apse\' | \'live-in\'');
    } else if (environment === 'test') {
      Track.error('Adyen V2 session created in the test environment, if this is not intended set adyen_v2_environment setting to live');
    }
    
    let config = {
      environment,
      clientKey,
      session: {
        id,
        sessionData,
      },
      analytics: {
        enabled: props.enableAdyenV2Analytics,
      },
      onSubmit: (state, component) => {
        let paymentMethodData = state?.data?.paymentMethod;
        const type = paymentMethodData?.type ? paymentMethodData.type : 'UNKNOWN TYPE';
        
        if (!paymentMethodData) {
          Track.error(`Adyen V2 onSubmit error for ${component?.props?.type} - state.data.paymentMethod is missing, attempting to continue`);
        }
        
        try {
          doMetric(
            'adyenV2',
            [
              {name: 'onSubmitDataPaymentMethodType', value: type},
            ],
          );
        } catch (error) {
          Track.track(`Unable to track Adyen V2 onSubmit Data. ${error}`);
        }
        
        let billingAddress = null;
        
        if (state.data?.billingAddress) {
          billingAddress = getAPIBillingAddressFromAdyenV2BillingAddress(state.data.billingAddress);
        }
        
        if (state.data?.storePaymentMethod !== undefined) {
          paymentMethodData = {...paymentMethodData, storePaymentMethod: state.data.storePaymentMethod};
        }
        
        props.setAdyenV2PlaceOrderData(paymentMethodData, billingAddress);
      },
      onAdditionalDetails: (state, component) => {
        const dataReceived = !!state.data;
        
        try {
          doMetric(
            'adyenV2',
            [
              {name: 'onAdditionalDetailsReceived', value: dataReceived},
            ],
          );
        } catch (error) {
          Track.track(`Unable to track Adyen V2 onAdditionalDetails Data. ${error}`);
        }
        
        if (!dataReceived) {
          Track.error(`Adyen V2 onAdditionalDetails error for ${component?.props?.type} - state.data is missing, attempting to continue`);
        }
        
        props.sendAdyenV2ActionResponse(state?.data);
      },
      onPaymentCompleted: (result, component) => {
        // Classing this as an error because in all cases Adyen should redirect back to the order processing page
        // for us for redirect, and we should forward to the order processing page in the case of QR codes
        Track.error(`Adyen V2 onPaymentCompleted called for ${component} with result ${result}`);
        
        forwardToURL('/checkout/processing');
      },
      onError: (error, component) => {
        Track.error(`Adyen V2 config onError: ${error?.name}, ${error?.message}, ${error.stack} for ${component}`);
      },
      // Optional Configuration
      showPayButton: false,
      locale: snakeToKebabCase(props.locale), // Set translations as per: https://docs.adyen.com/online-payments/web-components/localization-components
    };
    
    if (props.adyenV2PaymentMethodsConfiguration) {
      // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
      config = {
        ...config,
        paymentMethodsConfiguration: props.adyenV2PaymentMethodsConfiguration,
      };
    }
  
    if (props.adyenV2AllowedSubTypes) {
      config = {
        ...config,
        allowPaymentMethods: props.adyenV2AllowedSubTypes,
      };
    }
  
    if (props.adyenV2BlockedSubTypes) {
      config = {
        ...config,
        removePaymentMethods: props.adyenV2BlockedSubTypes,
      };
    }
    
    return config;
  };
  
  useEffect(() => {
    AdyenV2CheckoutLazyLoader()
      .then((AdyenV2Checkout) => {
        if (props.adyenV2SessionData) {
          const {clientKey, sessionId, sessionData} = props.adyenV2SessionData;
          if (clientKey && sessionId && sessionData && !props.adyenV2Checkout) {
            const config = getAdyenConfiguration(clientKey, sessionId, sessionData);
            const createCheckout = async () => AdyenV2Checkout.default(config);

            try {
              createCheckout()
                .then((checkout) => {
                  try {
                    doMetric(
                      'adyenV2',
                      [
                        {name: 'initialised', value: true},
                      ],
                    );
                  } catch (error) {
                    Track.track(`Error tracking Adyen V2 initialisation ${error}`);
                  }
      
                  props.handleAdyenV2CheckoutInitialisation(checkout);
                })
                .catch((error) => {
                  Track.error(`Error creating Adyen V2 Checkout, error: ${error}`);
                  props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.ERROR);
                });
            } catch (error) {
              Track.error(`Error creating Adyen V2 Checkout from NPM package, error: ${error}`);
              props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.ERROR);
            }
          } else {
            Track.error(`Data missing for Adyen V2, adyenV2Checkout=${!!props.adyenV2Checkout} clientKey=${clientKey} sessionId=${sessionId} sessionData=${sessionData}`);
            props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.ERROR);
          }
        }
      })
      .catch((error) => {
        Track.track(`Error getting response from adyen-web ${error}`);
        props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.ERROR);
      });
  }, [props.adyenV2SessionData]);
  
  return null;
}

useAdyenV2SessionLoader.propTypes = {
  adyenV2SessionData: PropTypes.shape({
    clientKey: PropTypes.string,
    sessionData: PropTypes.string,
    sessionId: PropTypes.string,
  }),
  adyenV2Environment: PropTypes.string,
  locale: PropTypes.string.isRequired,
  adyenV2Checkout: PropTypes.object.isRequired,
  adyenV2PaymentMethodsConfiguration: PropTypes.object,
  enableAdyenV2Analytics: PropTypes.bool,
  adyenV2AllowedSubTypes: PropTypes.arrayOf(PropTypes.string),
  adyenV2BlockedSubTypes: PropTypes.arrayOf(PropTypes.string),
  handleAdyenV2CheckoutInitialisation: PropTypes.func.isRequired,
  setAdyenV2PlaceOrderData: PropTypes.func.isRequired,
  sendAdyenV2ActionResponse: PropTypes.func.isRequired,
};
